/**
 * Dashboard: the pipeline at a glance, plus the recent proposals table.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ApiError,
  SUMMARY_STATUSES,
  endpoints,
  type ProposalStatus,
  type ProposalSummary,
} from '../lib/api';
import { useApiErrorHandler, useAuth } from '../lib/auth';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusBadge,
  formatDate,
  formatDateTime,
  formatMoney,
} from '../components/ui';
import {
  DuplicateDialog,
  type DuplicateChoices,
} from './wizard/FinalizationPanels';

const SUMMARY_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  READY: 'Ready',
  FINAL: 'Final',
  SUBMITTED: 'Submitted',
  WON: 'Won',
};

export function DashboardPage() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();

  const [counts, setCounts] = useState<Record<ProposalStatus, number> | null>(null);
  const [proposals, setProposals] = useState<ProposalSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<ProposalSummary | null>(null);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');

  const load = useCallback(async () => {
    try {
      setError(null);
      const [summary, list] = await Promise.all([
        endpoints.proposalSummary(scope),
        endpoints.proposals({ scope, limit: 25 }),
      ]);
      setCounts(summary.counts);
      setProposals(list.items);
    } catch (caught) {
      setError(handleError(caught));
    }
  }, [handleError, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const withBusy = async (id: string, action: () => Promise<void>) => {
    setBusyId(id);
    setNotice(null);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(handleError(caught));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Duplication is a two-step action on purpose. The dialog asks which
   * commercial information carries over before anything is created, because a
   * price agreed with one client silently reappearing in another's proposal is
   * the mistake this whole feature exists to prevent.
   */
  const duplicate = (choices: DuplicateChoices) => {
    const source = duplicating;
    if (!source) return;
    void withBusy(source.proposalId, async () => {
      const result = await endpoints.duplicateProposal(source.proposalId, choices);
      setDuplicating(null);
      navigate(`/proposals/${result.proposal.proposalId}`);
    });
  };

  const generate = (proposalId: string) =>
    withBusy(proposalId, async () => {
      try {
        const result = await endpoints.generateDocument(proposalId);
        setNotice(
          result.scan.ok
            ? `Document generated (version ${result.version}).`
            : `Generated with ${result.scan.summary.totalCritical} unresolved placeholder(s).`,
        );
        await load();
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 422) {
          setError(`${caught.message} Open the proposal to complete it.`);
          return;
        }
        throw caught;
      }
    });

  const download = (proposalId: string) =>
    withBusy(proposalId, async () => {
      const result = await endpoints.downloadUrl(proposalId);
      // The URL is a short-lived, server-issued grant; navigating to it starts
      // the download without exposing the object key anywhere else.
      window.location.assign(result.url);
    });

  const changeStatus = (proposalId: string, status: ProposalStatus) =>
    withBusy(proposalId, async () => {
      await endpoints.changeStatus(proposalId, status);
      await load();
    });

  return (
    <div className="space-y-6">
      {duplicating && (
        <DuplicateDialog
          proposalNumber={duplicating.proposalNumber}
          clientCompanyName={duplicating.clientCompanyName}
          busy={busyId === duplicating.proposalId}
          onCancel={() => setDuplicating(null)}
          onConfirm={duplicate}
        />
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Dashboard</h1>
          <p className="mt-0.5 text-[13px] text-muted">
            Create a client-ready proposal in two to five minutes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasRole('ADMIN') && (
            <div className="flex rounded-md border border-rule p-0.5" role="group" aria-label="Proposal scope">
              {(['mine', 'all'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={scope === value}
                  onClick={() => setScope(value)}
                  className={[
                    'rounded px-3 py-1 text-[13px] font-medium transition-colors',
                    scope === value
                      ? 'bg-tint text-teal'
                      : 'text-body hover:text-ink',
                  ].join(' ')}
                >
                  {value === 'mine' ? 'Mine' : 'All'}
                </button>
              ))}
            </div>
          )}
          <Button onClick={() => navigate('/proposals/new')}>New Proposal</Button>
        </div>
      </div>

      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {SUMMARY_STATUSES.map((status) => (
          <div
            key={status}
            className="rounded-lg border border-rule bg-white px-4 py-3.5"
          >
            <p className="text-[12px] font-medium uppercase tracking-wide text-muted">
              {SUMMARY_LABELS[status]}
            </p>
            {counts ? (
              <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">
                {counts[status] ?? 0}
              </p>
            ) : (
              <Skeleton className="mt-2 h-7 w-10" />
            )}
          </div>
        ))}
      </div>

      <Card
        title="Recent proposals"
        description={scope === 'all' ? 'All proposals' : 'Proposals you own'}
      >
        {!proposals ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        ) : proposals.length === 0 ? (
          <EmptyState
            title="No proposals yet"
            description="Choose one of the five approved proposal types to get started."
            action={<Button onClick={() => navigate('/proposals/new')}>New Proposal</Button>}
          />
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-rule text-left text-[11px] uppercase tracking-wide text-muted">
                  <th scope="col" className="px-5 py-2.5 font-semibold">Proposal #</th>
                  <th scope="col" className="px-3 py-2.5 font-semibold">Client</th>
                  <th scope="col" className="px-3 py-2.5 font-semibold">Type</th>
                  <th scope="col" className="px-3 py-2.5 font-semibold">Account owner</th>
                  <th scope="col" className="px-3 py-2.5 text-right font-semibold">Investment</th>
                  <th scope="col" className="px-3 py-2.5 font-semibold">Status</th>
                  <th scope="col" className="px-3 py-2.5 font-semibold">Created</th>
                  <th scope="col" className="px-3 py-2.5 font-semibold">Modified</th>
                  <th scope="col" className="px-5 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((proposal) => (
                  <tr
                    key={proposal.proposalId}
                    className="border-b border-rule last:border-0 hover:bg-tint"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/proposals/${proposal.proposalId}`}
                        className="font-medium text-teal hover:underline"
                      >
                        {proposal.proposalNumber}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-ink">
                      {proposal.clientCompanyName || (
                        <span className="text-muted">Not set</span>
                      )}
                    </td>
                    <td className="px-3 py-3">{proposal.templateName}</td>
                    <td className="px-3 py-3">{proposal.accountOwnerName}</td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatMoney(proposal.investmentTotal, proposal.currency)}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={proposal.status} />
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-muted">
                      {formatDate(proposal.createdAt)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-muted">
                      {formatDateTime(proposal.updatedAt)}
                    </td>
                    <td className="px-5 py-3">
                      <RowActions
                        proposal={proposal}
                        busy={busyId === proposal.proposalId}
                        onOpen={() => navigate(`/proposals/${proposal.proposalId}`)}
                        onDuplicate={() => setDuplicating(proposal)}
                        onGenerate={() => generate(proposal.proposalId)}
                        onDownload={() => download(proposal.proposalId)}
                        onChangeStatus={(status) => changeStatus(proposal.proposalId, status)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Row actions: Open, Duplicate, Generate, Download, Change status. */
function RowActions({
  proposal,
  busy,
  onOpen,
  onDuplicate,
  onGenerate,
  onDownload,
  onChangeStatus,
}: {
  proposal: ProposalSummary;
  busy: boolean;
  onOpen(): void;
  onDuplicate(): void;
  onGenerate(): void;
  onDownload(): void;
  onChangeStatus(status: ProposalStatus): void;
}) {
  const TRANSITIONS: Record<ProposalStatus, ProposalStatus[]> = {
    DRAFT: ['READY', 'EXPIRED'],
    READY: ['DRAFT', 'FINAL', 'EXPIRED'],
    FINAL: ['READY', 'SUBMITTED', 'EXPIRED'],
    SUBMITTED: ['WON', 'LOST', 'EXPIRED'],
    WON: [],
    LOST: ['SUBMITTED'],
    EXPIRED: ['DRAFT'],
  };
  const options = TRANSITIONS[proposal.status];

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button variant="ghost" size="sm" onClick={onOpen}>
        Open
      </Button>
      <Button variant="ghost" size="sm" onClick={onDuplicate} disabled={busy}>
        Duplicate
      </Button>
      <Button variant="ghost" size="sm" onClick={onGenerate} loading={busy}>
        Generate
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDownload}
        disabled={busy || proposal.latestDocumentVersion === null}
        title={
          proposal.latestDocumentVersion === null
            ? 'Generate the document first'
            : 'Download the latest version'
        }
      >
        Download
      </Button>
      <label className="sr-only" htmlFor={`status-${proposal.proposalId}`}>
        Change status for {proposal.proposalNumber}
      </label>
      <select
        id={`status-${proposal.proposalId}`}
        value=""
        disabled={busy || options.length === 0}
        onChange={(event) => {
          const next = event.target.value as ProposalStatus;
          if (next) onChangeStatus(next);
        }}
        className="rounded-md border border-rule bg-white px-2 py-1 text-[12px] text-body disabled:opacity-50"
      >
        <option value="">Status…</option>
        {options.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
    </div>
  );
}
